import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ReactFacebookFailureResponse, ReactFacebookLoginInfo } from 'react-facebook-login';
import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { FormInputText } from '../../inputs/FormInputText';
import { FormInputPassword } from '../../inputs/FormInputPassword';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { AuthorizationForm } from '../../../models/AuthorizationForm';
import { TextButton, TextButtonClassNames } from '../../commons/buttons/TextButton';
import { LoginWithSocials } from '../LoginWithSocials';
import { ApiError } from '../../../models/ApiError';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { authSlice } from '../../../store/reducers/AuthSlice';
import { ButtonClassNames, InputRegexPattern, LoginRouteNames } from '../../../models/Common';
import { authorizationApi } from '../../../services/apiServices/user/authorization';
import { AppleLoginResponse, TypeOfSocials } from '../../../models/Socials';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import {
  googleLoginURL,
  isTwoFactorEnabled,
  electronicCommunicationServicesDocLink,
} from '../../../constants/configuration';
import { useSocialAuthorization } from '../../../hooks/useSocialAuthorization';
import { parseJwt } from '../../../utils';

interface AuthProps {
  handleLink: (link: LoginRouteNames) => void;
  setIsPageToTop: (setIsPageToTop: boolean) => void;
  fromLocation: {
    pathname: string;
    search: string;
  };
}

export const Auth: FC<AuthProps> = ({ handleLink, setIsPageToTop, fromLocation }) => {
  const { t } = useTranslation();
  const { url } = useRouteMatch();
  const methods = useForm<AuthorizationForm>({
    mode: 'onSubmit',
    defaultValues: { loginAccountEmail: '', password: '' },
  });
  const history = useHistory();
  const { handleSubmit, setError, watch } = methods;
  const dispatch = useAppDispatch();
  const { authForm } = useAppSelector((stateAuth) => stateAuth.authReducer);
  const { setCredentials, setTwoFactorAuthCredentials, setFormError } = authSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [auth, authState] = authorizationApi.useCombinedAuthorizationMutation();
  const isSocials = true;
  const { socialAuth } = useSocialAuthorization(fromLocation);

  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      const userGoogleInfo = parseJwt(credentialResponse.credential);
      const isOnline = 'email' in userGoogleInfo;
      if (isOnline) {
        await socialAuth({
          type: TypeOfSocials.Google,
          socialUserId: userGoogleInfo.sub,
          email: userGoogleInfo.email,
        });
      }
    },
    cancel_on_tap_outside: true,
  });

  useEffect(() => {
    setIsPageToTop(false);
  }, []);

  const handleAuthSubmit = async (AuthState: AuthorizationForm) => {
    dispatch(setFormError(null));
    try {
      await auth({ auth_id: AuthState.loginAccountEmail, password: AuthState.password })
        .unwrap()
        .then((credentials) => {
          if (isTwoFactorEnabled && credentials.auth_type === 'internet') {
            dispatch(
              setTwoFactorAuthCredentials({
                accessToken: credentials.access_token,
                refreshToken: credentials.refresh_token,
              }),
            );
            history.push(`${url}${LoginRouteNames.TwoFactorAuth}`);
          } else {
            dispatch(
              setCredentials({
                accessToken: credentials.access_token,
                refreshToken: credentials.refresh_token,
              }),
            );
            history.replace(`${fromLocation.pathname}${fromLocation.search}`);
          }
        });
    } catch (err) {
      const error = err as ApiError;
      dispatch(setFormError(error?.data?.error.message));
    }
  };

  const handleAppleLogin = async (res: AppleLoginResponse) => {
    const isOnline = 'authorization' in res;
    if (isOnline) {
      const jwt = parseJwt(res.authorization.id_token);
      await socialAuth({
        type: TypeOfSocials.Apple,
        socialUserId: jwt.sub,
        email: jwt.email,
      });
    }
  };

  const handleGoogleLoginError = (err: string | undefined) => {
    if (err) {
      customEnqueueSnackbar(err, 'error');
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const info = await fetch(googleLoginURL, {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
      });
      const userGoogleInfo = await info.json();
      const isOnline = 'email' in userGoogleInfo;
      if (isOnline) {
        await socialAuth({
          type: TypeOfSocials.Google,
          socialUserId: userGoogleInfo.sub,
          email: userGoogleInfo.email,
        });
      }
    },
    onError: (errorResponse) => handleGoogleLoginError(errorResponse.error_description),
  });

  const handleFacebookLogin = async (res: ReactFacebookLoginInfo | ReactFacebookFailureResponse) => {
    const isSuccess = 'userID' in res;

    if (isSuccess) {
      await socialAuth({
        type: TypeOfSocials.Facebook,
        socialUserId: res.userID,
        email: res.email || '',
        accessToken: res.accessToken,
      });
    }
  };

  const handleLoginSMS = () => {
    handleLink(LoginRouteNames.RequestCodeSmsForm);
  };
  const handleForgotPassword = () => {
    handleLink(LoginRouteNames.ForgotPasswordForm);
  };

  useEffect(() => {
    if (authForm.error) {
      setError('loginAccountEmail', {
        type: 'server',
        message: authForm.error,
      });
      setError('password', {
        type: 'server',
        message: '',
      });
    }
  }, [handleAuthSubmit]);

  const getLoginEmailPatter = (value: string) => {
    if (value.includes('@') || value.includes('.')) {
      return InputRegexPattern.Email;
    }
    return InputRegexPattern.InternetLogin;
  };

  return (
    <div className="auth-container">
      <p className="auth-container__title login-page__title">{t('auth.authorization')}</p>
      <FormProvider {...methods}>
        <form className="auth-container__form" onSubmit={handleSubmit(handleAuthSubmit)}>
          <FormInputText
            name="loginAccountEmail"
            className="primary"
            label={t('auth.userPhoneNameAccountEmail')}
            isRequired
            minLength={3}
            pattern={getLoginEmailPatter(watch('loginAccountEmail'))}
            // placeholder={t('auth.enterAuthLogin')}
            fieldHasName
          />
          <FormInputPassword
            name="password"
            className="primary"
            label={t('auth.enterAuthPassword')}
            isRequired
            minLength={6}
            maxLength={16}
            // placeholder={t('auth.enterAuthPassword')}
            fieldHasName
          />
          <div className="auth-container__form__remember-me-container">
            <TextButton label={t('auth.loginWithSMS')} handleClick={handleLoginSMS} className="primary" isArrow />
            <TextButton
              label={t('auth.forgotPassword')}
              handleClick={handleForgotPassword}
              className={`${TextButtonClassNames.PrimaryActive} auth-container__form__forgot-pass`}
              isArrow={false}
            />
          </div>
          <CustomMUIButton
            label={t('auth.logIn')}
            className={ButtonClassNames.PrimaryFulfilled}
            isBold
            type="submit"
            isLoading={authState.isLoading}
          />
          <div className="auth-container__form__agreement-label">
            {t('auth.agreeToRulesOf')}{' '}
            <a href={electronicCommunicationServicesDocLink} target="_blank" rel="noreferrer">
              {t('auth.termsOfService')}
            </a>
          </div>
        </form>
      </FormProvider>
      <div className="auth-container__additional-options">
        {isSocials && (
          <>
            <Divider className="auth-container__additional-options__divider-text">{t('auth.loginWithSocials')}</Divider>
            <LoginWithSocials
              isGoogleAvailable
              isFacebookAvailable={false}
              isAppleAvailable
              handleAppleLogin={handleAppleLogin}
              handleGoogleLogin={handleGoogleLogin}
              handleFacebookLogin={handleFacebookLogin}
            />
          </>
        )}
      </div>
    </div>
  );
};
